// #69 History and HistoryRow (DC-20): the one event list for every timeline, history and audit view (Candidate
// timeline, Requisition timeline, request history, negotiation History, scheduling history, Audit). A vertical list
// on a 2 px --border-strong spine; each row has a 10 px dot in the event's duty hue, the actor's avatar and name, the
// event sentence, the date and the time (the time in mono), the source as a Tag and the linked artifacts as chips that
// deep-link (F: the only focus stops). An `audit` row adds Old → New (Diff inline) and the Reason. Rows are grouped by
// day under a date rule; `compact` is Home's short form (one line a row, at most 8 rows, no day rules). Append-only:
// no control inside a row. `loading`: skeleton rows; empty: one --text-3 line. A masked row (DC-28) is
// MaskedHistoryRow.

import { Fragment, type ReactNode } from "react";
import { EmptyState } from "../core/EmptyState";
import { Avatar } from "../core/Person";
import { Skeleton } from "../core/Skeleton";
import { Tag } from "../core/Tag";
import { SectionTitle } from "../core/Text";
import type { Tone } from "../labels";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { DEFAULT_TIME_ZONE, formatDate, formatTime, toEpoch, type Instant } from "../lib/time";
import { Diff } from "./Diff";
import { MaskedHistoryRow } from "./Masked";

export interface HistoryArtifact {
  /** The artifact's name (a reference id, a document word). */
  label: string;
  href?: string | undefined;
  hs?: Hs | undefined;
}

export interface HistoryEvent {
  /** A stable key. */
  id: string;
  actor?: { name: string; hueKey?: string | undefined } | undefined;
  /** The event sentence, in his words (a label or a filled template). */
  sentence: ReactNode;
  at: Instant;
  /** The event's duty hue (DS §1.4); default info (blue). */
  tone?: Tone | undefined;
  source?: ReactNode | undefined;
  artifacts?: readonly HistoryArtifact[] | undefined;
  /** The audit form: Old → New and the Reason. */
  audit?: { old: ReactNode; new: ReactNode; reason?: ReactNode | undefined } | undefined;
  /** Outside the viewer's scope: drawn as one masked row. */
  masked?: boolean | undefined;
  /** A masked group: how many trimmed events the masked row stands for. */
  maskedCount?: number | undefined;
}

export interface HistoryRowProps {
  event: HistoryEvent;
  compact?: boolean | undefined;
  timeZone?: string | undefined;
}

function When({ at, timeZone }: { at: Instant; timeZone: string }) {
  return (
    <time className="mc-hwhen" dateTime={new Date(toEpoch(at)).toISOString()}>
      {formatDate(at, timeZone)} <span className="mc-mono">{formatTime(at, { timeZone })}</span>
    </time>
  );
}

function Artifact({ artifact }: { artifact: HistoryArtifact }) {
  const hot = useHs(artifact.hs);
  if (artifact.href === undefined) return <span className="mc-chip mc-chip-sm mc-hart">{artifact.label}</span>;
  return <a className="mc-chip mc-chip-sm mc-hart mc-hart-link" href={artifact.href} {...hot}>{artifact.label}</a>;
}

export function HistoryRow({ event, compact, timeZone = DEFAULT_TIME_ZONE }: HistoryRowProps) {
  if (event.masked) return <MaskedHistoryRow when={formatDate(event.at, timeZone)} count={event.maskedCount} />;
  const tone = event.tone ?? "info";
  return (
    <li className={cx("mc-hrow", `mc-hrow-${tone}`, event.audit && "mc-hrow-audit", compact && "mc-hrow-c")}>
      <span className="mc-hdot" aria-hidden="true" />
      <div className="mc-hbody">
        <div className="mc-hhead">
          {event.actor ? (
            <span className="mc-hactor">
              <Avatar name={event.actor.name} hueKey={event.actor.hueKey} size={24} decorative />
              <b>{event.actor.name}</b>
            </span>
          ) : null}
          {compact ? <span className="mc-hsent">{event.sentence}</span> : null}
          <When at={event.at} timeZone={timeZone} />
        </div>
        {compact ? null : <p className="mc-hsent">{event.sentence}</p>}
        {event.audit && !compact ? (
          <Diff original={event.audit.old} revised={event.audit.new} reason={event.audit.reason} className="mc-haudit" />
        ) : null}
        {!compact && (event.source !== undefined || (event.artifacts && event.artifacts.length)) ? (
          <div className="mc-hfoot">
            {event.source !== undefined && event.source !== null ? <Tag>{event.source}</Tag> : null}
            {event.artifacts?.map((artifact, index) => <Artifact key={`${artifact.label}-${index}`} artifact={artifact} />)}
          </div>
        ) : null}
      </div>
    </li>
  );
}

export interface HistoryProps {
  events: readonly HistoryEvent[];
  /** The list's name (*Candidate timeline*, *Requisition timeline*, *History*, *Audit*). */
  label: string;
  /** Home's short form: one line a row, at most `max` (8) rows, no day rules. */
  compact?: boolean | undefined;
  max?: number | undefined;
  /** Day rules (default on, off when compact). */
  days?: boolean | undefined;
  loading?: boolean | number | undefined;
  /** The empty line's words (default "—"). */
  empty?: ReactNode | undefined;
  timeZone?: string | undefined;
  className?: string | undefined;
}

export function History({ events, label, compact, max = 8, days, loading, empty, timeZone = DEFAULT_TIME_ZONE, className }: HistoryProps) {
  const classes = cx("mc-hist", compact && "mc-hist-c", className);
  if (loading) {
    const rows = loading === true ? 3 : loading;
    return (
      <div className={classes} role="group" aria-label={label} aria-busy="true">
        <ol className="mc-hist-l">
          {Array.from({ length: rows }, (_, index) => (
            <li key={index} className="mc-hrow mc-hrow-skel" aria-hidden="true">
              <span className="mc-hdot" />
              <div className="mc-hbody"><Skeleton width="40%" height={12} /><Skeleton width="80%" /></div>
            </li>
          ))}
        </ol>
      </div>
    );
  }
  if (events.length === 0) {
    return (
      <div className={classes} role="group" aria-label={label}>
        <EmptyState inline>{empty ?? "—"}</EmptyState>
      </div>
    );
  }
  const shown = compact ? events.slice(0, max) : events;
  const grouped = days ?? !compact;
  const groups: { day: string; items: HistoryEvent[] }[] = [];
  for (const event of shown) {
    const day = grouped ? formatDate(event.at, timeZone) : "";
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.items.push(event);
    else groups.push({ day, items: [event] });
  }
  return (
    <div className={classes} role="group" aria-label={label}>
      {groups.map((group, index) => (
        <Fragment key={`${group.day}-${index}`}>
          {grouped ? <SectionTitle className="mc-hday">{group.day}</SectionTitle> : null}
          <ol className="mc-hist-l">
            {group.items.map((event) => <HistoryRow key={event.id} event={event} compact={compact} timeZone={timeZone} />)}
          </ol>
        </Fragment>
      ))}
    </div>
  );
}
